'use client';

import type { FC } from 'react';
import { logger } from '../utils/logger';
import type { PlatformResponse, AdMeshTheme } from '../types/index';
import { AdMeshSDK } from '../sdk/AdMeshSDK';
import { AdMeshViewabilityTracker } from './AdMeshViewabilityTracker';

export interface AdMeshFollowupProps {
  recommendation: PlatformResponse;
  theme?: AdMeshTheme;
  sdk: AdMeshSDK;
  sessionId: string;
  onExecuteQuery?: (query: string) => void | Promise<void>;
}

const PlusIcon = ({ className, size = 20 }: { className?: string; size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

/**
 * AdMeshFollowup - Sponsored Follow-up Component
 * 
 * Displays sponsored follow-up suggestions as optional fields on any recommendation.
 * Follow-ups use the same recommendation_id as the primary ad and are rendered separately
 * in a followups_container_id. This component handles all exposure and engagement tracking
 * internally - platforms only need to provide an onExecuteQuery hook for query execution.
 * 
 * @example
 * ```tsx
 * <AdMeshFollowup
 *   recommendation={recommendation}
 *   sdk={sdk}
 *   sessionId={sessionId}
 *   theme={theme}
 *   onExecuteQuery={(query) => {
 *     // Platform's query execution logic
 *     executeQuery(query);
 *   }}
 * />
 * ```
 */
export const AdMeshFollowup: FC<AdMeshFollowupProps> = ({
  recommendation,
  theme: _theme,
  sdk,
  sessionId,
  onExecuteQuery,
}) => {
  const followupSuggestion =
    recommendation.recommendation?.creative?.follow_up_suggestion &&
    recommendation.tracking?.followup_engagement_url &&
    recommendation.tracking?.followup_exposure_url
      ? {
          query: recommendation.recommendation.creative.follow_up_suggestion,
          engagement_url: recommendation.tracking.followup_engagement_url,
          exposure_url: recommendation.tracking.followup_exposure_url,
        }
      : null;
  const followupQuery = followupSuggestion?.query;
  const followupEngagementUrl = followupSuggestion?.engagement_url;
  const followupExposureUrl = followupSuggestion?.exposure_url;
  const recommendationId = recommendation.recommendation_id;
  const productId = recommendation.recommendation?.offerId || recommendationId;

  // Validate required fields
  if (!followupQuery || !followupEngagementUrl || !followupExposureUrl) {
    logger.log('[AdMeshFollowup] Missing followup_suggestion.query, engagement_url, or exposure_url - not rendering');
    return null;
  }

  // Handle follow-up click/selection
  const handleFollowupClick = async () => {
    try {
      // Fire engagement tracking (SDK handles this automatically)
      if (followupEngagementUrl && recommendationId) {
        logger.log('[AdMeshFollowup] 🎯 Firing follow-up engagement tracking');
        await sdk.fireFollowupEngagement(followupEngagementUrl, recommendationId, sessionId);
      }

      // Execute query via platform hook
      if (onExecuteQuery && followupQuery) {
        logger.log(`[AdMeshFollowup] 🔍 Executing query: ${followupQuery}`);
        await onExecuteQuery(followupQuery);
      } else {
        logger.warn('[AdMeshFollowup] ⚠️ onExecuteQuery not provided - cannot execute query');
      }
    } catch (error) {
      logger.error('[AdMeshFollowup] ❌ Error handling follow-up click:', error);
    }
  };

  // Platform-native styling (matching Perplexica's "Related" section)
  // We use inline styles that can be overridden, but default to blending in

  return (
    <AdMeshViewabilityTracker
      productId={productId}
      recommendationId={recommendationId || ''}
      exposureUrl={followupExposureUrl}
      sessionId={sessionId}
      className="admesh-followup-container"
      style={{
        width: '100%',
      }}
    >
      <div className="flex flex-col space-y-3 text-sm admesh-followup-wrapper">
        {/* Divider matching platform style */}
        <div className="h-px w-full bg-[#E5E5E5] dark:bg-[#262626] admesh-divider" />

        <div
          onClick={handleFollowupClick}
          className="cursor-pointer flex flex-row justify-between font-medium space-x-2 items-center group"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleFollowupClick();
            }
          }}
          aria-label={`Sponsored follow-up: ${followupQuery}`}
        >
          <p className="transition duration-200 text-[#000] dark:text-[#FFF] hover:text-[#24A0ED] admesh-followup-text">
            {followupQuery}
          </p>
          <div className="flex flex-row items-center space-x-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 italic admesh-ad-label">
              Ad
            </span>
            <PlusIcon
              size={20}
              className="text-[#24A0ED] flex-shrink-0 admesh-plus-icon"
            />
          </div>
        </div>
      </div>
    </AdMeshViewabilityTracker>
  );
};
